// src/components/ContactForm.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const ContactForm = ({ initialMessage = '' }) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const [status, setStatus] = useState(''); // '', 'sending', 'success', 'error'

  useEffect(() => {
    if (initialMessage) {
      setFormData((prev) => ({ ...prev, message: initialMessage }));
    }
  }, [initialMessage]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      setStatus('error');
      return;
    }

    setStatus('sending');

    try {
      if (!window.grecaptcha) {
        throw new Error('reCAPTCHA not loaded');
      }

      // Ejecuta reCAPTCHA v3
      const captchaToken = await window.grecaptcha.execute(
        process.env.REACT_APP_RECAPTCHA_SITE_KEY,
        { action: 'contact' }
      );

      // 🔥 IMPORTANTE: usar ruta relativa (NGINX proxy)
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          captchaToken,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Request failed');
      }

      setStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      console.error('Contact form error:', err);
      setStatus('error');
    }
  };

  return (
    <section
      id="contacto"
      className="relative z-10 py-32 px-6 text-white overflow-hidden"
    >
      <div className="container mx-auto max-w-4xl">
        {/* Título */}
        <div className="text-center mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl font-light tracking-tight mb-6"
          >
            {t('contact_title')}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl text-slate-400 font-light"
          >
            {t('contact_subtitle')}
          </motion.p>
        </div>

        {/* Formulario */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div className="bg-slate-900/70 backdrop-blur-xl rounded-3xl p-10 md:p-14 border border-slate-800">
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid md:grid-cols-2 gap-8">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder={t('contact_name_placeholder')}
                  className="w-full px-6 py-4 bg-slate-800/50 border border-slate-700 rounded-xl"
                />

                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder={t('contact_email_placeholder')}
                  className="w-full px-6 py-4 bg-slate-800/50 border border-slate-700 rounded-xl"
                />
              </div>

              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder={t('contact_phone_placeholder')}
                className="w-full px-6 py-4 bg-slate-800/50 border border-slate-700 rounded-xl"
              />

              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="6"
                placeholder={t('contact_message_placeholder')}
                className="w-full px-6 py-5 bg-slate-800/50 border border-slate-700 rounded-xl resize-none"
              />

              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full py-5 rounded-xl font-semibold text-lg bg-gradient-to-r from-orange-500 to-orange-600 disabled:opacity-60"
              >
                {status === 'sending'
                  ? t('contact_sending')
                  : t('contact_submit')}
              </button>

              {status === 'success' && (
                <p className="text-center text-green-400 text-xl">
                  {t('contact_success')}
                </p>
              )}

              {status === 'error' && (
                <p className="text-center text-red-400 text-xl">
                  {t('contact_error')}
                </p>
              )}
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactForm;
